import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Clock, MapPin, CheckCircle, Loader2, ChefHat, Truck, XCircle, Star, MessageSquare, RotateCcw, Download, RefreshCw, Search, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import OrderTracking from '../components/OrderTracking';
import ReorderButton from '../components/ReorderButton';
import { RateOrderButton, OrderRatingBadge } from '../components/OrderRatingDialog';

const statusConfig = {
  placed: { label: 'Placed', color: 'bg-yellow-500', icon: Clock },
  accepted: { label: 'Accepted', color: 'bg-blue-500', icon: CheckCircle },
  pending: { label: 'Pending', color: 'bg-gray-500', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-500', icon: CheckCircle },
  preparing: { label: 'Preparing', color: 'bg-orange-500', icon: ChefHat },
  ready: { label: 'Ready', color: 'bg-green-500', icon: Package },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-purple-500', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-600', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-green-600', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', icon: XCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle }
};

const CustomerOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderRatings, setOrderRatings] = useState({}); // Track ratings per order
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    if (!token) {
      navigate('/');
      return;
    }
    fetchCustomerData();
    fetchOrders();
    
    // Auto-refresh orders every 30 seconds for live updates
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  // Handle inline order rating (quick rating from list)
  const handleQuickOrderRating = async (orderId, rating) => {
    try {
      const order = orders.find(o => o.id === orderId);
      await api.post('/orders/rate', {
        order_id: orderId,
        rating: rating
      }, {
        headers: { 'x-customer-phone': order?.customer_phone }
      });
      
      // Update local state
      setOrderRatings(prev => ({ ...prev, [orderId]: rating }));
      toast.success('Thanks for rating!');
      fetchOrders(); // Refresh to get updated ratings
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rating');
    }
  };

  // Handle reorder
  const handleReorder = async (order) => {
    // Check availability and add items to cart
    try {
      const outletId = order.outlet_id;
      const response = await api.get(`/products?outlet_id=${outletId}`);
      const products = response.data;
      
      const cartItems = [];
      const unavailable = [];
      
      for (const item of order.items) {
        const product = products.find(p => p.id === item.product_id);
        
        if (!product || product.available === false || product.stock === 0) {
          unavailable.push(item.product_name);
          continue;
        }
        
        const quantity = product.stock !== undefined && product.stock < item.quantity 
          ? product.stock 
          : item.quantity;
        
        cartItems.push({
          product_id: item.product_id,
          product_name: item.product_name,
          price: product.price, // Use current price
          quantity: quantity
        });
      }
      
      if (cartItems.length > 0) {
        localStorage.setItem('fishOnFireCart', JSON.stringify(cartItems));
        toast.success(`${cartItems.length} item(s) added to cart!`);
        
        if (unavailable.length > 0) {
          toast.warning(`${unavailable.length} item(s) unavailable: ${unavailable.slice(0, 2).join(', ')}${unavailable.length > 2 ? '...' : ''}`);
        }
        
        navigate('/');
      } else {
        toast.error('No items available for reorder');
      }
    } catch (error) {
      console.error('Error reordering:', error);
      // Fallback: add items without checking
      const cartItems = order.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        price: item.price,
        quantity: item.quantity
      }));
      localStorage.setItem('fishOnFireCart', JSON.stringify(cartItems));
      toast.success('Items added to cart!');
      navigate('/');
    }
  };

  // Handle invoice download
  const handleDownloadInvoice = async (orderId, orderNumber) => {
    try {
      const response = await api.get(`/orders/${orderId}/invoice`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded!');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const fetchCustomerData = async () => {
    try {
      const response = await api.get('/customers/me');
      setCustomer(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerData');
        navigate('/');
      }
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get('/customers/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerData');
        navigate('/');
      } else {
        toast.error('Failed to load orders');
      }
    } finally {
      setLoading(false);
    }
  };

  // Search orders
  const searchOrders = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', '100');
      
      const response = await api.get(`/customers/orders/search?${params.toString()}`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error searching orders:', error);
      toast.error('Failed to search orders');
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setStatusFilter('all');
    fetchOrders();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    return statusConfig[status] || statusConfig.placed;
  };

  // Separate active and past orders
  const activeStatuses = ['placed', 'accepted', 'preparing', 'ready', 'out_for_delivery'];
  const activeOrders = orders.filter(o => activeStatuses.includes(o.status));
  const pastOrders = orders.filter(o => !activeStatuses.includes(o.status));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF4500] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" className="text-white p-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                My Orders
              </h1>
              {customer && (
                <p className="text-sm text-gray-400">{customer.phone}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Search Bar */}
        <div className="bg-[#121212] rounded-lg p-4 mb-6 border border-white/10">
          <div className="flex flex-wrap gap-2">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder="Search by order # or item name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchOrders()}
                  className="pl-10 bg-[#050505] border-white/10 text-white"
                  data-testid="customer-order-search"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="From"
                className="w-[130px] bg-[#050505] border-white/10 text-white"
                data-testid="customer-order-date-from"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="To"
                className="w-[130px] bg-[#050505] border-white/10 text-white"
                data-testid="customer-order-date-to"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] bg-[#050505] border-white/10 text-white" data-testid="customer-order-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={searchOrders}
              className="bg-[#FF4500] hover:bg-[#FF5722] text-white"
              disabled={searching}
              data-testid="customer-search-btn"
            >
              {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
            {(searchQuery || dateFrom || dateTo || statusFilter !== 'all') && (
              <Button
                onClick={clearSearch}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
        
        {orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 mx-auto text-gray-600 mb-4" strokeWidth={1.5} />
            <h2 className="text-xl font-bold text-white mb-2">No orders yet</h2>
            <p className="text-gray-400 mb-6">Your order history will appear here</p>
            <Link to="/">
              <Button className="bg-[#FF4500] hover:bg-[#FF5722] text-white rounded-full">
                Browse Menu
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Active Orders */}
            {activeOrders.length > 0 && (
              <section>
                <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue' }}>
                  Active Orders ({activeOrders.length})
                </h2>
                <div className="space-y-4">
                  {activeOrders.map(order => {
                    const statusInfo = getStatusInfo(order.status);
                    const StatusIcon = statusInfo.icon;
                    return (
                      <Card 
                        key={order.id} 
                        className="bg-[#121212] border-white/10 border-l-4 border-l-[#FF4500]"
                        data-testid={`active-order-${order.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="text-white font-bold text-lg">{order.order_number}</p>
                              <p className="text-gray-400 text-sm">{formatDate(order.created_at)}</p>
                            </div>
                            <Badge className={`${statusInfo.color} text-white flex items-center gap-1`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusInfo.label}
                            </Badge>
                          </div>

                          <div className="space-y-2 mb-3">
                            {order.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-gray-300">
                                  {item.quantity}x {item.product_name}
                                </span>
                                <span className="text-gray-400">{formatCurrency(item.price * item.quantity)}</span>
                              </div>
                            ))}
                          </div>

                          <div className="flex items-center justify-between pt-3 border-t border-white/10">
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              {order.order_type === 'delivery' ? (
                                <>
                                  <MapPin className="w-4 h-4" />
                                  <span>Delivery</span>
                                </>
                              ) : order.order_type === 'dine_in' ? (
                                <>
                                  <Package className="w-4 h-4" />
                                  <span>Dine In</span>
                                </>
                              ) : order.order_type === 'takeaway' ? (
                                <>
                                  <Package className="w-4 h-4" />
                                  <span>Takeaway</span>
                                </>
                              ) : (
                                <>
                                  <Package className="w-4 h-4" />
                                  <span>Pickup</span>
                                </>
                              )}
                            </div>
                            <p className="text-xl font-bold text-[#FF4500]">
                              {formatCurrency(order.total_amount)}
                            </p>
                          </div>

                          <Link to={`/track/${order.id}`} className="block mt-3">
                            <Button 
                              variant="outline" 
                              className="w-full border-[#FF4500]/50 text-[#FF4500] hover:bg-[#FF4500]/10"
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectedOrder(order);
                              }}
                            >
                              Track Order
                            </Button>
                          </Link>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Past Orders */}
            {pastOrders.length > 0 && (
              <section>
                <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue' }}>
                  Past Orders ({pastOrders.length})
                </h2>
                <div className="space-y-4">
                  {pastOrders.map(order => {
                    const statusInfo = getStatusInfo(order.status);
                    const StatusIcon = statusInfo.icon;
                    const currentRating = orderRatings[order.id] || order.order_rating || 0;
                    
                    return (
                      <Card 
                        key={order.id} 
                        className="bg-[#121212] border-white/10"
                        data-testid={`past-order-${order.id}`}
                      >
                        <CardContent className="p-4">
                          {/* Order Header - Clickable to open details */}
                          <div 
                            className="cursor-pointer"
                            onClick={() => setSelectedOrder(order)}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="text-white font-medium">{order.order_number}</p>
                                <p className="text-gray-500 text-sm">{formatDate(order.created_at)}</p>
                              </div>
                              <div className="text-right">
                                <Badge className={`${statusInfo.color} text-white flex items-center gap-1 mb-1`}>
                                  <StatusIcon className="w-3 h-3" />
                                  {statusInfo.label}
                                </Badge>
                                <p className="text-lg font-bold text-[#FF4500]">
                                  {formatCurrency(order.total_amount)}
                                </p>
                              </div>
                            </div>

                            {/* Order Items Summary */}
                            <div className="text-gray-400 text-sm mb-3">
                              {order.items.map((item, idx) => (
                                <span key={idx}>
                                  {item.quantity} x {item.product_name}
                                  {idx < order.items.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Rating & Actions Row */}
                          <div className="flex items-center justify-between pt-3 border-t border-white/10">
                            {/* Inline Star Rating */}
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400 text-sm">Rate</span>
                              <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={star}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleQuickOrderRating(order.id, star);
                                    }}
                                    className="focus:outline-none transition-transform hover:scale-110"
                                  >
                                    <Star
                                      className={`w-6 h-6 ${
                                        star <= currentRating 
                                          ? 'fill-yellow-400 text-yellow-400' 
                                          : 'text-gray-500 hover:text-yellow-400/50'
                                      }`}
                                    />
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2">
                              {/* Download Invoice Button */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadInvoice(order.id, order.order_number);
                                }}
                                className="border-white/20 text-white hover:bg-white/10"
                              >
                                <Download className="w-4 h-4" />
                              </Button>

                              {/* Reorder Button */}
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReorder(order);
                                }}
                                className="bg-[#FF4500] hover:bg-[#FF5722] text-white"
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Reorder
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
      
      {/* Order Tracking Modal */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="bg-[#1a1a1a] max-w-lg max-h-[90vh] overflow-y-auto p-0 [&>button]:hidden border-white/10">
          {selectedOrder && (
            <OrderTracking 
              order={selectedOrder} 
              onClose={() => setSelectedOrder(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerOrders;
